import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Slider,
  Avatar,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  SportsEsports as HobbyIcon,
  Category as CategoryIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteImageIcon
} from '@mui/icons-material';
import api from '../../services/api';
import toast from 'react-hot-toast';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const HobbiesManager = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [hobbies, setHobbies] = useState([]);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentType, setCurrentType] = useState('category');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    display_order: 0,
    category_id: '',
    icon_file: null,
    image_file: null,
    existing_icon: '',
    existing_image: '',
    is_active: 1
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [imagePreview, setImagePreview] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchHobbies();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/hobbies/categories');
      setCategories(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  const fetchHobbies = async (categoryId = null) => {
    try {
      const url = categoryId ? `/hobbies/hobbies?category_id=${categoryId}` : '/hobbies/hobbies';
      const response = await api.get(url);
      setHobbies(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch hobbies');
    }
  };

  const handleOpenDialog = (type, item = null) => {
    setCurrentType(type);
    setEditingItem(item);
    setImagePreview(null);
    setIconPreview(null);
    
    if (item) {
      if (type === 'category') {
        setFormData({
          name: item.name || '',
          description: item.description || '',
          display_order: item.display_order || 0,
          category_id: '',
          icon_file: null,
          image_file: null,
          existing_icon: item.icon_url || '',
          existing_image: '',
          is_active: item.is_active === 1
        });
        setIconPreview(item.icon_url);
      } else {
        setFormData({
          name: item.name || '',
          description: item.description || '',
          display_order: item.display_order || 0,
          category_id: item.category_id || '',
          icon_file: null,
          image_file: null,
          existing_icon: item.icon_url || '',
          existing_image: item.image_url || '',
          is_active: item.is_active === 1
        });
        setImagePreview(item.image_url);
        setIconPreview(item.icon_url);
      }
    } else {
      setFormData({
        name: '',
        description: '',
        display_order: 0,
        category_id: '',
        icon_file: null,
        image_file: null,
        existing_icon: '',
        existing_image: '',
        is_active: 1
      });
    }
    setDialogOpen(true);
  };

  const handleFileChange = (type, file) => {
    if (type === 'icon') {
      setFormData({ ...formData, icon_file: file });
      setIconPreview(URL.createObjectURL(file));
    } else if (type === 'image') {
      setFormData({ ...formData, image_file: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }

    if (currentType === 'hobby' && !formData.category_id) {
      toast.error('Please select a category');
      return;
    }

    setUploading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('display_order', formData.display_order);
      formDataToSend.append('is_active', formData.is_active ? 1 : 0);
      
      if (currentType === 'category') {
        if (formData.icon_file) {
          formDataToSend.append('icon', formData.icon_file);
        } else if (formData.existing_icon) {
          formDataToSend.append('existing_icon', formData.existing_icon);
        }
      } else {
        formDataToSend.append('category_id', formData.category_id);
        if (formData.image_file) {
          formDataToSend.append('image', formData.image_file);
        } else if (formData.existing_image) {
          formDataToSend.append('existing_image', formData.existing_image);
        }
        if (formData.icon_file) {
          formDataToSend.append('icon', formData.icon_file);
        } else if (formData.existing_icon) {
          formDataToSend.append('existing_icon', formData.existing_icon);
        }
      }
      
      let url;
      if (currentType === 'category') {
        url = editingItem ? `/hobbies/categories/${editingItem.id}` : '/hobbies/categories';
      } else {
        url = editingItem ? `/hobbies/hobbies/${editingItem.id}` : '/hobbies/hobbies';
      }
      
      const method = editingItem ? 'put' : 'post';
      const response = await api[method](url, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        toast.success(`${currentType === 'category' ? 'Category' : 'Hobby'} ${editingItem ? 'updated' : 'added'} successfully`);
        setDialogOpen(false);
        
        // Reset form
        setFormData({
          name: '',
          description: '',
          display_order: 0,
          category_id: '',
          icon_file: null,
          image_file: null,
          existing_icon: '',
          existing_image: '',
          is_active: 1
        });
        setImagePreview(null);
        setIconPreview(null);
        
        // Refresh data
        if (currentType === 'category') {
          fetchCategories();
        } else {
          fetchHobbies();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to save`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (type, id, name) => {
    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      try {
        let url;
        if (type === 'category') {
          url = `/hobbies/categories/${id}`;
        } else {
          url = `/hobbies/hobbies/${id}`;
        }
        
        const response = await api.delete(url);
        if (response.data.success) {
          toast.success(`${type === 'category' ? 'Category' : 'Hobby'} deleted successfully`);
          
          if (type === 'category') {
            fetchCategories();
          } else {
            fetchHobbies();
          }
        }
      } catch (error) {
        toast.error(error.response?.data?.message || `Failed to delete`);
      }
    }
  };

  const handleCategoryFilter = async (categoryId) => {
    setCategoryFilter(categoryId);
    if (categoryId === 'all') {
      await fetchHobbies();
    } else {
      await fetchHobbies(categoryId);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id == categoryId);
    return category ? category.name : 'N/A';
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        Hobbies Management
      </Typography>

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab icon={<CategoryIcon />} label="Categories" />
          <Tab icon={<HobbyIcon />} label="Hobbies" />
        </Tabs>

        {/* Categories Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <TextField
              size="small"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('category')}
              sx={{
                background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
              }}
            >
              Add Category
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Icon</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Display Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories
                  .filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map((category) => (
                    <TableRow key={category.id} hover>
                      <TableCell>
                        {category.icon_url ? (
                          <Avatar src={category.icon_url} sx={{ width: 40, height: 40 }} />
                        ) : (
                          <CategoryIcon />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="500">{category.name}</Typography>
                      </TableCell>
                      <TableCell>{category.description || '-'}</TableCell>
                      <TableCell>{category.display_order}</TableCell>
                      <TableCell>
                        <Chip 
                          label={category.is_active ? 'Active' : 'Inactive'}
                          color={category.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenDialog('category', category)} color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete('category', category.id, category.name)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {categories.length === 0 && !loading && (
            <Box textAlign="center" py={5}>
              <Typography color="textSecondary">No categories found. Click "Add Category" to create one.</Typography>
            </Box>
          )}
        </TabPanel>

        {/* Hobbies Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box mb={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search hobbies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Category</InputLabel>
                  <Select
                    value={categoryFilter}
                    label="Filter by Category"
                    onChange={(e) => handleCategoryFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {categories.map(category => (
                      <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog('hobby')}
                  sx={{
                    background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
                  }}
                >
                  Add Hobby
                </Button>
              </Grid>
            </Grid>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Image</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Hobby Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Display Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hobbies
                  .filter(h => h.name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        {item.image_url ? (
                          <Avatar src={item.image_url} variant="rounded" sx={{ width: 40, height: 40 }} />
                        ) : item.icon_url ? (
                          <Avatar src={item.icon_url} sx={{ width: 40, height: 40 }} />
                        ) : (
                          <HobbyIcon />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getCategoryName(item.category_id)} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="500">{item.name}</Typography>
                      </TableCell>
                      <TableCell>{item.description || '-'}</TableCell>
                      <TableCell>{item.display_order}</TableCell>
                      <TableCell>
                        <Chip 
                          label={item.is_active ? 'Active' : 'Inactive'}
                          color={item.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenDialog('hobby', item)} color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete('hobby', item.id, item.name)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {hobbies.length === 0 && !loading && (
            <Box textAlign="center" py={5}>
              <Typography color="textSecondary">No hobbies found. Click "Add Hobby" to create one.</Typography>
            </Box>
          )}
        </TabPanel>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)', 
          color: '#fff'
        }}>
          {editingItem ? 'Edit' : 'Add'} {currentType === 'category' ? 'Category' : 'Hobby'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {currentType === 'hobby' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category_id}
                    label="Category"
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    required
                  >
                    {categories.map(category => (
                      <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            
            {currentType === 'category' && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Category Icon</Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  {iconPreview && (
                    <Avatar src={iconPreview} sx={{ width: 60, height: 60 }} />
                  )}
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                  >
                    Upload Icon
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => handleFileChange('icon', e.target.files[0])}
                    />
                  </Button>
                  {iconPreview && (
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => {
                        setFormData({ ...formData, icon_file: null, existing_icon: '' });
                        setIconPreview(null);
                      }}
                    >
                      <DeleteImageIcon />
                    </IconButton>
                  )}
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Recommended size: 64x64px. Max 5MB
                </Typography>
              </Grid>
            )}
            
            {currentType === 'hobby' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Hobby Image</Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    {imagePreview && (
                      <Avatar src={imagePreview} variant="rounded" sx={{ width: 60, height: 60 }} />
                    )}
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                    >
                      Upload Image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => handleFileChange('image', e.target.files[0])}
                      />
                    </Button>
                    {imagePreview && (
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => {
                          setFormData({ ...formData, image_file: null, existing_image: '' });
                          setImagePreview(null);
                        }}
                      >
                        <DeleteImageIcon />
                      </IconButton>
                    )}
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    Recommended size: 200x200px. Max 5MB
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Hobby Icon</Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    {iconPreview && (
                      <Avatar src={iconPreview} sx={{ width: 60, height: 60 }} />
                    )}
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                    >
                      Upload Icon
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => handleFileChange('icon', e.target.files[0])}
                      />
                    </Button>
                    {iconPreview && (
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => {
                          setFormData({ ...formData, icon_file: null, existing_icon: '' });
                          setIconPreview(null);
                        }}
                      >
                        <DeleteImageIcon />
                      </IconButton>
                    )}
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    Recommended size: 64x64px. Max 5MB
                  </Typography>
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <Typography gutterBottom>Display Order</Typography>
              <Slider
                value={formData.display_order}
                onChange={(e, newValue) => setFormData({ ...formData, display_order: newValue })}
                min={0}
                max={100}
                valueLabelDisplay="auto"
                marks={[
                  { value: 0, label: '0' },
                  { value: 25, label: '25' },
                  { value: 50, label: '50' },
                  { value: 75, label: '75' },
                  { value: 100, label: '100' }
                ]}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active === 1 || formData.is_active === true}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={uploading}
            sx={{
              background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
            }}
          >
            {uploading ? <CircularProgress size={24} /> : (editingItem ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HobbiesManager;